import { c as _c } from "react/compiler-runtime";
import { X } from 'lucide-react';
import { Label } from '@/components/ui/label';
import { HubLookup } from '@/components/lookups/HubLookup';
import type { LookupOption } from '@/components/ui/lookup';
import type { ApiContent } from '@/lib/api/types';
export interface LinkedHub {
  id: number;
  title: string;
}
interface HubLinkerProps {
  selectedHubs: LookupOption<ApiContent>[];
  onSelectedHubsChange: (hubs: LookupOption<ApiContent>[]) => void;
  label?: string;
  description?: string;
  disabled?: boolean;
}

/**
 * Reusable component for linking objects to hubs (content records of type 'hub').
 * Displays a lookup to search/select hubs and shows selected hubs as removable pills.
 */
export function HubLinker(t0) {
  const $ = _c(19);
  const {
    selectedHubs,
    onSelectedHubsChange,
    label: t1,
    description: t2,
    disabled
  } = t0;
  const label = t1 === undefined ? "Linked Hubs" : t1;
  const description = t2 === undefined ? "Link this record to hubs so it appears in those hub views" : t2;
  let t3;
  if ($[0] !== label) {
    t3 = <Label>{label}</Label>;
    $[0] = label;
    $[1] = t3;
  } else {
    t3 = $[1];
  }
  let t4;
  if ($[2] !== onSelectedHubsChange || $[3] !== selectedHubs) {
    t4 = hub => {
      if (hub && !selectedHubs.find(h => h.id === hub.id)) {
        onSelectedHubsChange([...selectedHubs, hub]);
      }
    };
    $[2] = onSelectedHubsChange;
    $[3] = selectedHubs;
    $[4] = t4;
  } else {
    t4 = $[4];
  }
  let t5;
  if ($[5] !== disabled || $[6] !== t4) {
    t5 = <HubLookup value={null} onChange={t4} placeholder="Search hubs to link..." disabled={disabled} />;
    $[5] = disabled;
    $[6] = t4;
    $[7] = t5;
  } else {
    t5 = $[7];
  }
  let t6;
  if ($[8] !== disabled || $[9] !== onSelectedHubsChange || $[10] !== selectedHubs) {
    t6 = selectedHubs.length > 0 && <div className="flex flex-wrap gap-2 mt-2">{selectedHubs.map(hub_0 => <div key={hub_0.id} className="flex items-center gap-2 bg-emerald-50 text-emerald-800 border border-emerald-200 px-3 py-1 rounded-md text-sm"><span>{hub_0.label}</span><button type="button" onClick={() => onSelectedHubsChange(selectedHubs.filter(h_0 => h_0.id !== hub_0.id))} className="hover:text-destructive cursor-pointer" disabled={disabled}><X className="h-3 w-3" /></button></div>)}</div>;
    $[8] = disabled;
    $[9] = onSelectedHubsChange;
    $[10] = selectedHubs;
    $[11] = t6;
  } else {
    t6 = $[11];
  }
  let t7;
  if ($[12] !== description) {
    t7 = <p className="text-sm text-muted-foreground">{description}</p>;
    $[12] = description;
    $[13] = t7;
  } else {
    t7 = $[13];
  }
  let t8;
  if ($[14] !== t3 || $[15] !== t5 || $[16] !== t6 || $[17] !== t7) {
    t8 = <div className="space-y-2">{t3}{t5}{t6}{t7}</div>;
    $[14] = t3;
    $[15] = t5;
    $[16] = t6;
    $[17] = t7;
    $[18] = t8;
  } else {
    t8 = $[18];
  }
  return t8;
}

/**
 * Parse hub IDs from systemTags (format: @hub=<id>)
 */
export function parseHubIdsFromSystemTags(systemTags: string[] | undefined | null): number[] {
  if (!systemTags) return [];
  return systemTags.filter(tag => tag.startsWith('@hub=')).map(tag => parseInt(tag.replace('@hub=', ''), 10)).filter(id => !isNaN(id));
}

/**
 * Build hub system tags from hub IDs
 */
export function buildHubSystemTags(hubIds: number[]): string[] {
  return hubIds.map(id => `@hub=${id}`);
}